import React, { useState, useEffect } from 'react';
import { FaArrowLeft, FaDownload, FaSearch, FaEye, FaUser, FaBuilding, FaBook, FaGlobe, FaCalendarAlt, FaCheckCircle } from 'react-icons/fa';
import { useNavigate, useParams } from 'react-router-dom';
import { Modal, Row, Col, Button, Spinner } from 'react-bootstrap';
import { useAuth } from '../../../contexts/AuthContext';
import api from '../../../api/apiClient';
import { toast } from 'react-toastify';

const SurveyResponses = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const [survey, setSurvey] = useState(null);
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedResponse, setSelectedResponse] = useState(null);

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [surveyRes, responsesRes] = await Promise.all([
        api.surveys.getById(id),
        api.surveys.getDetailedResponses(id)
      ]);
      
      setSurvey(responsesRes.data.survey);
      setResponses(responsesRes.data.data || []);
    } catch (error) {
      console.error('Error fetching responses:', error);
      toast.error('Failed to load responses');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => setSearchTerm(e.target.value);
  const handleFilterTypeChange = (e) => setFilterType(e.target.value);

  const handleViewDetails = (response) => {
    setSelectedResponse(response);
    setShowDetailModal(true);
  };

  const handleCloseDetailModal = () => {
    setShowDetailModal(false);
    setSelectedResponse(null);
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const response = await api.surveys.exportResponses(id);
      
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `survey_${id}_responses_${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('Responses exported successfully!');
    } catch (error) {
      console.error('Error exporting responses:', error);
      toast.error('Failed to export responses');
    } finally {
      setExporting(false);
    }
  };

  const normalizeAnswerValue = (value) => {
    if (Array.isArray(value)) return value;
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) return '';
      if ((trimmed.startsWith('[') && trimmed.endsWith(']')) || (trimmed.startsWith('{') && trimmed.endsWith('}'))) {
        try {
          const parsed = JSON.parse(trimmed);
          return Array.isArray(parsed) ? parsed : trimmed;
        } catch {
          return trimmed;
        }
      }
      if (trimmed.includes('\n')) {
        return trimmed.split('\n').map(line => line.trim()).filter(Boolean);
      }
      return trimmed;
    }
    return value ?? '';
  };

  const renderAnswerContent = (value, questionType) => {
    const normalized = normalizeAnswerValue(value);
    if (Array.isArray(normalized)) {
      return (
        <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
          {normalized.map((item, i) => <li key={i}>{item}</li>)}
        </ul>
      );
    }
    if (!normalized) return <span className="text-gray-400 italic">No response</span>;
    if (questionType === 'textarea') {
      return <div className="whitespace-pre-wrap text-sm text-gray-700">{normalized}</div>;
    }
    return <span className="font-medium text-gray-800">{normalized}</span>;
  };

  const getSurveyTypeIcon = () => {
    if (!survey) return FaGlobe;
    if (survey.survey_type === 'global') return FaGlobe;
    if (survey.course_id) return FaBook;
    return FaBuilding;
  };

  const getSurveyTypeLabel = () => {
    if (!survey) return '';
    if (survey.survey_type === 'global') return 'Global Survey';
    if (survey.course_id && survey.course) return `Course: ${survey.course.name}`;
    if (survey.organization) return survey.organization.name;
    return 'Organization Survey';
  };

  const TypeIcon = getSurveyTypeIcon();

  const filteredResponses = responses.filter(response => {
    if (!response?.user) return false;
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = response.user.name.toLowerCase().includes(searchLower) ||
                         response.user.email.toLowerCase().includes(searchLower);

    let matchesType = true;
    if (filterType !== 'all') {
      if (filterType === 'global') matchesType = survey?.survey_type === 'global';
      else if (filterType === 'organization') matchesType = survey?.survey_type === 'organization' && !survey?.course_id;
      else if (filterType === 'course') matchesType = !!survey?.course_id;
    }

    return matchesSearch && matchesType;
  });

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-96">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-purple-600 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-4 md:p-6 bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 min-h-screen">
    

            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
              <div className="flex items-center gap-4">
                <button
            onClick={() => navigate('/admin/surveys')}
                  className="flex items-center gap-2 px-4 py-2 bg-white border border-purple-300 text-purple-700 rounded-lg shadow-md hover:bg-purple-50 hover:shadow-lg transition-all duration-200 text-sm font-medium"
                >
                  <FaArrowLeft className="w-4 h-4" /> Back
                </button>
                 <div>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">
              {survey?.title || 'Survey Responses'}
            </h2>
            <div className="flex items-center gap-2 mt-2">
              {/* <TypeIcon className="w-5 h-5 text-purple-600" />
              <span className="text-sm font-medium text-gray-700">{getSurveyTypeLabel()}</span> */}
            </div>
          </div>
        
              </div>
                    <button
          onClick={handleExport}
          disabled={responses.length === 0 || exporting}
          className=" border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 py-2 px-8"
        >
          {exporting ? (
            <>Exporting...</>
          ) : (
            <>
              <FaDownload className="w-5 h-5" />
              Export CSV
            </>
          )}
        </button>
            </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-xl shadow-md p-5 text-center">
          <p className="text-xs font-medium text-purple-600 uppercase tracking-wider">Total Responses</p>
          <p className="text-4xl font-bold text-purple-700 mt-2">{responses.length}</p>
        </div>
        <div className="bg-white rounded-xl shadow-md p-5 text-center">
          <p className="text-xs font-medium text-pink-600 uppercase tracking-wider">Filtered</p>
          <p className="text-4xl font-bold text-pink-700 mt-2">{filteredResponses.length}</p>
        </div>
        <div className="bg-white rounded-xl shadow-md p-5 text-center">
          <p className="text-xs font-medium text-blue-600 uppercase tracking-wider">Completion Rate</p>
          <p className="text-4xl font-bold text-blue-700 mt-2">
            {responses.length > 0 ? Math.round((responses.length / responses.length) * 100) : 0}%
          </p>
        </div>
      </div>

      {/* Search & Filter */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="flex shadow-sm rounded-xl overflow-hidden bg-white">
          <span className="inline-flex items-center px-4 text-purple-600 bg-white border-r border-purple-200">
            <FaSearch className="w-5 h-5" />
          </span>
          <input
            type="text"
            placeholder="Search by name or email..."
            value={searchTerm}
            onChange={handleSearch}
            className="border-0 focus:ring-2 focus:ring-purple-500 text-sm sm:text-base form-control"
          />
        </div>

        <select
          value={filterType}
          onChange={handleFilterTypeChange}
          className="rounded-lg sm:rounded-xl shadow-sm border-purple-200 focus:ring-2 focus:ring-purple-500 text-xs sm:text-sm form-select"
        >
          <option value="all">All Levels</option>
          <option value="global">Global Survey</option>
          <option value="organization">Organization Only</option>
          <option value="course">Course-linked</option>
        </select>
      </div>

      {/* Table */}
      {filteredResponses.length === 0 ? (
        <div className="text-center py-16 bg-white/80 backdrop-blur rounded-2xl shadow-lg border border-purple-100">
          <div className="text-6xl text-purple-300 mb-4">Empty Inbox</div>
          <p className="text-purple-700 font-medium text-lg">
            {responses.length === 0 
              ? 'No responses yet'
              : 'No responses match your filters'}
          </p>
          <p className="text-gray-600 mt-2">
            {responses.length === 0 
              ? 'Responses will appear here once users complete the survey.'
              : 'Try adjusting your search or filter settings.'}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl shadow-xl bg-white">
          <table className="m-0 min-w-max text-xs sm:text-sm table table-bordered table-hover">
            <thead className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
              <tr>
                <th className="py-3 px-3 text-left font-bold">#</th>
                <th className="py-3 px-3 text-left font-bold">User</th>
                <th className="py-3 px-3 text-left font-bold">Email</th>
                <th className="py-3 px-3 text-left font-bold">Submitted</th>
                <th className="py-3 px-3 text-center font-bold">Answers</th>
                <th className="py-3 px-3 text-center font-bold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredResponses.map((response, index) => (
                <tr key={response.response_id} className="border-b border-purple-100 hover:bg-purple-50/50 transition">
                  <td className="py-3 px-3">
                    <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-purple-100 text-purple-700 font-bold">
                      {index + 1}
                    </span>
                  </td>
                  <td className="py-3 px-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold">
                        {response.user.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="font-semibold text-gray-800">{response.user.name}</div>
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-3 text-gray-600">{response.user.email}</td>
                  <td className="py-3 px-3">
                    <div className="flex items-center gap-2 text-gray-700">
                      <FaCalendarAlt className="w-4 h-4 text-purple-500" />
                      {new Date(response.submitted_at).toLocaleString()}
                    </div>
                  </td>
                  <td className="py-3 px-3 text-center">
                    <span className="inline-flex items-center gap-1 px-4 py-2 rounded-full bg-green-100 text-green-800 font-medium text-sm">
                      <FaCheckCircle className="w-4 h-4" />
                      {response.answers.length}
                    </span>
                  </td>
                  <td className="py-3 px-3 text-center">
                    <div className="d-flex gap-1 justify-content-center">
                      <Button
                        bsPrefix="custom-hamburger"
                        onClick={() => handleViewDetails(response)}
                        title="View Details"
                        className="border-gray-300 text-gray-600 hover:bg-gray-100 rounded p-1"
                      >
                        <FaEye className="w-5 h-5" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Response Detail Modal (react-bootstrap pattern) */}
      <Modal show={showDetailModal} onHide={handleCloseDetailModal} centered fullscreen="sm-down">
        <Modal.Header closeButton closeVariant="white" className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0">
          <Modal.Title className="text-lg font-bold">Response Details</Modal.Title>
        </Modal.Header>

        <Modal.Body className="p-4 sm:p-5">
          {/* Respondent Info */}
          <div className="bg-gray-50 rounded-xl p-4 mb-4">
            <h4 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
              <FaUser className="text-purple-600" />
              Respondent Information
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Name:</span>
                <p className="font-semibold text-gray-800">{selectedResponse?.user?.name}</p>
              </div>
              <div>
                <span className="text-gray-600">Email:</span>
                <p className="font-semibold text-gray-800">{selectedResponse?.user?.email}</p>
              </div>
              <div>
                <span className="text-gray-600">Submitted:</span>
                <p className="font-semibold text-gray-800 flex items-center gap-2">
                  <FaCalendarAlt className="text-purple-500" />
                  {selectedResponse ? new Date(selectedResponse.submitted_at).toLocaleString() : ''}
                </p>
              </div>
            </div>
          </div>

          {/* Answers */}
          <div>
            <h4 className="font-bold text-gray-800 mb-4">Answers ({selectedResponse?.answers?.length || 0})</h4>
            <div className="space-y-4">
              {(selectedResponse?.answers || []).map((answer, idx) => (
                <div key={idx} className="bg-white border border-purple-100 rounded-xl p-4 shadow-sm">
                  <div className="flex items-start gap-4">
                    <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-purple-100 text-purple-700 font-bold text-lg flex-shrink-0">
                      {idx + 1}
                    </span>
                    <div className="flex-1">
                      <p className="font-semibold text-gray-800 mb-2">{answer.question_text}</p>
                      <div className="bg-gray-50 rounded-lg p-3">
                        {renderAnswerContent(answer.answer, answer.question_type)}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer className="bg-gray-50 border-0">
          <div className="w-100 d-flex justify-content-end">
            <Button onClick={handleCloseDetailModal} className="bg-gray-200 text-gray-800 border-0 font-semibold rounded-lg px-4 py-2">
              Close
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default SurveyResponses;